import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 1000 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();

    /**
     * HB 2026-10-09 (Emilie: three pieces in the bag on the product page, no number on the collection page). A page
     * the browser brings back from its back-forward cache, serves again from its own cache on Back, or rendered ahead
     * of time, still shows the count from when it was rendered. The 10-second sessionStorage check above only covers
     * a count stamped seconds ago. So: after a Back/Forward navigation, a bfcache restore or a prerender activation,
     * the bubble asks the cart itself.
     */
    window.addEventListener('pageshow', this.onPageShow);
    document.addEventListener('prerenderingchange', this.onPrerenderingChange);
    const nav = performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
    if (nav && nav.type === 'back_forward') this.syncWithCart(true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
    document.removeEventListener('prerenderingchange', this.onPrerenderingChange);
  }

  /** @param {PageTransitionEvent} event */
  onPageShow = (event) => {
    if (event.persisted) this.syncWithCart(true);
  };

  onPrerenderingChange = () => {
    this.syncWithCart(false);
  };

  /**
   * Reads the cart and renders its count. With reloadIfStale, a page whose rendered count no longer matches the cart
   * (the bag changed while the page was away) is loaded again, so the drawer's lines are right too, not only the number.
   * @param {boolean} reloadIfStale
   */
  syncWithCart = async (reloadIfStale) => {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      if (typeof cart.item_count !== 'number') return;
      if (reloadIfStale && cart.item_count !== this.currentCartCount) {
        window.location.reload();
        return;
      }
      this.renderCartBubble(cart.item_count, false, false);
    } catch (_) {
      // Leave the bubble as it is.
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    // const itemCount = event.detail.data?.itemCount ?? 0;
    const reportedCount = event.detail.data?.itemCount;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    // this.renderCartBubble(itemCount, comingFromProductForm);
    /**
     * Some dispatchers send a source and nothing else. The card quick-add in
     * assets/product-custom.js does, for both the D2C size popup
     * (source: 'variant-popup-inner') and the B2B size-run matrix
     * (source: 'b2b-quick-add'), because neither keeps the /cart/add.js
     * response. Reading it as zero emptied the bubble on a SUCCESSFUL add and
     * stamped a zero cart-count into sessionStorage, so the wrong count also
     * survived into the next page load. An absent count now means ask the cart
     * rather than zero. Dispatchers that do pass a count are untouched,
     * including the product form's add-to-current-count path.
     */
    if (reportedCount == null) {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();

        this.renderCartBubble(cart.item_count, false);
      } catch (_) {
        // Leave the bubble as it is. The next page load renders the truth.
      }

      return;
    }

    this.renderCartBubble(reportedCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', itemCount > 0 && animate);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    const visibleCount = this.refs.cartBubbleCount.textContent;

    if (sessionStorageCount === visibleCount || sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
